<template>
    <div v-if="loading" class="text-center mt-5">
        <div class="spinner-border" role="status" style="width: 10rem; height: 10rem;"></div>
    </div>
    <div v-else-if="error" class="text-center mt-5">{{ $t('somethingWentWrong') }}</div>
    <article v-else id="show-chemical" class="container mb-5">
        <header id="chemical-page-header" class="p-2 rounded" :style="dangerColorStyle">
            <div class="row">
                <h1 class="col">
                    {{ chemical.name }}
                    <span
                        class="badge font-italic float-right"
                        v-if="chemical.decommissioned"
                    >({{ $t("decommissioned") }})</span>
                </h1>

                <span id="icon-container" class="col-2 col-md-1">
          <span
              v-if="this.chemical.partnerreferences.length > 0"
              v-b-toggle.partnerReferences
              id="help-icon-container"
          >
            <img
                alt=" "
                id="help-icon"
                class="img-fluid"
                style="cursor: pointer;"
                draggable="false"
                src="/assets/images/icons/info.png"
            />
          </span>

          <span id="dangercode-icon-container" style="display: none">
            <img alt="" :src="dangerCodeIconName" width="50px"/>
          </span>
        </span>
            </div>

            <div class="row">
                <span class="col-12 col-md-6">{{ $t("sdsRevisionDate") }}: {{ chemical.sds.revisionDate | formatDate }}</span>
                <span class="col-12 col-md-6">{{ $t("malCode") }}: {{ this.chemical.malcode || "none" }}</span>
                <span class="col-12 col-md-6" v-if="this.chemical.ufis && this.chemical.ufis.length">
                    {{ $t("ufi") }}: {{ this.commaSeparatedUFIs }}
                </span>
            </div>

            <div class="row">
                <div class="col-12 text-right font-italic" id="dangercode-container">
                    {{ $t('dangerCode', {color: this.$t(chemical.dangercode)}) }}
                </div>
            </div>

            <section v-if="this.chemical.partnerreferences.length > 0">
                <b-collapse id="partnerReferences" class="mt-4">
                    <h4>{{ $t("partnerReferences") }}</h4>
                    <ul class="row list-unstyled">
                        <li
                            class="col-12 col-sm-6 col-md-4 col-lg-3"
                            v-for="partnerreference in this.chemical.partnerreferences"
                            :key="partnerreference"
                        >
                            <span>{{ partnerreference }}</span>
                        </li>
                    </ul>
                </b-collapse>
            </section>
        </header>

        <main>
            <nav id="sds-link-button">
                <b-button
                    :disabled="isSafetyDatasheetMissing"
                    :href="sdsUrl"
                    block
                    class="mt-3"
                    target="_blank">
                    <h2>{{ isSafetyDatasheetMissing ? $t("noSdsFound") : $t("sds") }}</h2>
                </b-button>
            </nav>

            <section>
                <header>
                    <b-button v-b-toggle.pictograms block class="mt-3 mb-1">
                        <h2>{{ $t("pictograms") }}</h2>
                    </b-button>
                </header>
                <b-collapse visible id="pictograms">
                    <ul class="row list-unstyled">
                        <li
                            class="col-6 col-lg-3 mt-3 text-center"
                            v-for="(hazard, index) in hazards"
                            :key="index"
                        >
                            <img draggable="false" :src="hazard.imageUrl" :alt="hazard.description"/>
                        </li>
                    </ul>
                    <div class="row">
                        <div class="col text-right font-italic">
                            {{ $t("signalword", {word: this.$t(chemical.signalword)}) }}
                        </div>
                    </div>
                </b-collapse>
            </section>

            <section>
                <header>
                    <b-button v-b-toggle.euHstatementDetails block class="mt-3 mb-1">
                        <h2>{{ $t("hAndEuhStatementSection") }}</h2>
                    </b-button>
                </header>

                <b-collapse visible id="euHstatementDetails">
                    <h4 class="col">{{ $t("hstatements") }}</h4>
                    <ul class="container-fluid">
                        <li class="row my-2" v-for="statement in orderedHstatements" :key="statement.code">
                            <span class="col-4 col-md-2 align-self-center">{{ statement.code }}</span>
                            <span class="col">{{ statement.description }}</span>
                        </li>
                    </ul>

                    <h4 class="col">{{ $t("euhstatements") }}</h4>
                    <ul class="container-fluid">
                        <li class="row my-2" v-for="statement in orderedEUHstatements" :key="statement.code">
                            <span class="col-4 col-md-2 align-self-center">{{ statement.code }}</span>
                            <span class="col">{{ statement.description }}</span>
                        </li>
                    </ul>
                </b-collapse>
            </section>

            <section>
                <header>
                    <b-button v-b-toggle.pStatementDetails block class="mt-3 mb-1">
                        <h2 class="col">{{ $t("pstatements") }}</h2>
                    </b-button>
                </header>
                <b-collapse visible id="pStatementDetails">
                    <ul class="container-fluid">
                        <li class="row my-2" v-for="statement in orderedPstatements" :key="statement.code">
                            <span class="col-4 col-md-2 align-self-center">{{ statement.code }}</span>
                            <span class="col">{{ statement.description }}</span>
                        </li>
                    </ul>
                </b-collapse>
            </section>

            <section>
                <header>
                    <b-button v-b-toggle.substanceDetails block class="mt-3 mb-1">
                        <h2>{{ $t("substances") }}</h2>
                    </b-button>
                </header>

                <b-collapse visible id="substanceDetails">
                    <div v-if="carcenogenicSubstances && carcenogenicSubstances.length">
                        <div class="col">
                            <div class="row">
                                <h4 class="col">{{ $t("carcenogenic") }}</h4>
                                <span class="col-2 col-md-1 text-center">mg/m³</span>
                                <span class="col-2 col-md-1 text-center">ppm</span>
                            </div>
                        </div>
                        <ul class="container-fluid">
                            <li class="row" v-for="substance in carcenogenicSubstances" :key="substance.id">
                                <span class="col-4 col-md-2 align-self-center">{{ substance.casNumber }}</span>
                                <span class="col" style="white-space:pre-wrap;">{{ substance.name }}</span>
                                <span
                                    class="col-2 col-md-1 text-center"
                                >{{ substance.exposureLimitInMgPerCubicMeter || '-' }}</span>
                                <span
                                    class="col-2 col-md-1 text-center"
                                >{{ substance.exposureLimitInPartsPerMillion || '-' }}</span>
                            </li>
                        </ul>
                    </div>

                    <div v-if="candidateSubstances && candidateSubstances.length">
                        <div class="col">
                            <div class="row">
                                <h4 class="col">{{ $t("candidates") }}</h4>
                                <span class="col-2 col-md-1 text-center">mg/m³</span>
                                <span class="col-2 col-md-1 text-center">ppm</span>
                            </div>
                        </div>
                        <ul class="container-fluid">
                            <li class="row" v-for="substance in candidateSubstances" :key="substance.id">
                                <span class="col-4 col-md-2 align-self-center">{{ substance.casNumber }}</span>
                                <span class="col" style="white-space:pre-wrap;">{{ substance.name }}</span>
                                <span
                                    class="col-2 col-md-1 text-center"
                                >{{ substance.exposureLimitInMgPerCubicMeter || '-' }}</span>
                                <span
                                    class="col-2 col-md-1 text-center"
                                >{{ substance.exposureLimitInPartsPerMillion || '-' }}</span>
                            </li>
                        </ul>
                    </div>

                    <div v-if="endocrineDisruptorSubstances && endocrineDisruptorSubstances.length">
                        <div class="col">
                            <div class="row">
                                <h4 class="col">{{ $t("endocrineDisruptors") }}</h4>
                                <span class="col-2 col-md-1 text-center">mg/m³</span>
                                <span class="col-2 col-md-1 text-center">ppm</span>
                            </div>
                        </div>
                        <ul class="container-fluid">
                            <li class="row" v-for="substance in endocrineDisruptorSubstances" :key="substance.id">
                                <span class="col-4 col-md-2 align-self-center">{{ substance.casNumber }}</span>
                                <span class="col" style="white-space:pre-wrap;">{{ substance.name }}</span>
                                <span
                                    class="col-2 col-md-1 text-center"
                                >{{ substance.exposureLimitInMgPerCubicMeter || '-' }}</span>
                                <span
                                    class="col-2 col-md-1 text-center"
                                >{{ substance.exposureLimitInPartsPerMillion || '-' }}</span>
                            </li>
                        </ul>
                    </div>

                    <div v-if="volatileOrganicCompoundSubstances && volatileOrganicCompoundSubstances.length">
                        <div class="col">
                            <div class="row">
                                <h4 class="col">{{ $t("occupationalHealthValue") }}</h4>
                                <span class="col-2 col-md-1 text-center">mg/m³</span>
                                <span class="col-2 col-md-1 text-center">ppm</span>
                            </div>
                        </div>
                        <ul class="container-fluid">
                            <li
                                class="row"
                                v-for="substance in volatileOrganicCompoundSubstances"
                                :key="substance.id"
                            >
                                <span class="col-4 col-md-2 align-self-center">{{ substance.casNumber }}</span>
                                <span class="col" style="white-space:pre-wrap;">{{ substance.name }}</span>
                                <span
                                    class="col-2 col-md-1 text-center"
                                >{{ substance.exposureLimitInMgPerCubicMeter || '-' }}</span>
                                <span
                                    class="col-2 col-md-1 text-center"
                                >{{ substance.exposureLimitInPartsPerMillion || '-' }}</span>
                            </li>
                        </ul>
                    </div>

                    <div v-if="otherSubstances && otherSubstances.length">
                        <div class="col">
                            <div class="row">
                                <h4 class="col">{{ $t("others") }}</h4>
                                <span class="col-2 col-md-1 text-center">mg/m³</span>
                                <span class="col-2 col-md-1 text-center">ppm</span>
                            </div>
                        </div>
                        <ul class="container-fluid">
                            <li class="row" v-for="substance in otherSubstances" :key="substance.id">
                                <span class="col-4 col-md-2 align-self-center">{{ substance.casNumber }}</span>
                                <span class="col" style="white-space:pre-wrap;">{{ substance.name }}</span>
                                <span
                                    class="col-2 col-md-1 text-center"
                                >{{ substance.exposureLimitInMgPerCubicMeter || '-' }}</span>
                                <span
                                    class="col-2 col-md-1 text-center"
                                >{{ substance.exposureLimitInPartsPerMillion || '-' }}</span>
                            </li>
                        </ul>
                    </div>
                </b-collapse>
            </section>

            <section>
                <header>
                    <b-button v-b-toggle.protectionDetails block class="mt-3 mb-1">
                        <h2>{{ $t("protection") }}</h2>
                    </b-button>
                </header>
                <b-collapse visible id="protectionDetails">
                    <ul class="container-fluid">
                        <li class="row my-2">
                            <span class="col-4 col-md-2 align-self-center">{{ $t("skin") }}</span>
                            <span class="col">{{ $t("chemicalResistantGloves") }}</span>
                        </li>
                        <li class="row my-2">
                            <span class="col-4 col-md-2 align-self-center">{{ $t("eyes") }}</span>
                            <span class="col">{{ $t("goggles") }}</span>
                        </li>
                        <li class="row my-2">
                            <span class="col-4 col-md-2 align-self-center">{{ $t("inhalation") }}</span>
                            <span class="col" v-html="$t('halfMaskRespirator')"></span>
                        </li>
                    </ul>
                </b-collapse>
            </section>
        </main>
    </article>
</template>

<script>
import _ from "lodash";
import urljoin from "url-join";
import axios from "axios";

export default {
    name: "show-chemical",
    metaInfo() {
        return {
            title: this.$t("showChemicalsTitle"),
            htmlAttrs: {
                lang: this.$i18n.locale,
                amp: true
            }
        };
    },
    computed: {
        commaSeparatedUFIs() {
            return this.chemical.ufis.join(", ");
        },
        hazards() {
            return _.map(this.chemical.hazards, hazard => {
                return {
                    imageUrl: urljoin("/assets", hazard.imageUrl),
                    description: hazard.description
                };
            });
        },
        sdsUrl() {
            return urljoin("/api/v1/chemicals", this.id, "sds");
        },
        orderedHstatements() {
            return _.orderBy(this.chemical.hstatements, "code");
        },
        orderedEUHstatements() {
            return _.orderBy(this.chemical.euhstatements, "code");
        },
        orderedPstatements() {
            return _.orderBy(this.chemical.pstatements, "code");
        },
        carcenogenicSubstances() {
            return _.filter(this.chemical.substances, item => item.isCarcenogenic);
        },
        candidateSubstances() {
            return _.filter(this.chemical.substances, item => item.isCandidate);
        },
        endocrineDisruptorSubstances() {
            return _.filter(this.chemical.substances, item => item.isEndocrineDisruptor);
        },
        volatileOrganicCompoundSubstances() {
            return _.filter(
                this.chemical.substances,
                item => item.isVolatileOrganicCompound
            );
        },
        otherSubstances() {
            return _.filter(
                this.chemical.substances,
                item => !item.isCarcenogenic && !item.isVolatileOrganicCompound && !item.isCandidate && !item.isEndocrineDisruptor
            );
        },
        dangerCode() {
            return this.$t("dangerCode", {color: this.$t(this.chemical.dangercode)});
        },
        dangerCodeIconName() {
            const path = "/assets/icons/"

            if (this.chemical.dangercode.toUpperCase() === "RED") {
                return urljoin(path, "close.png");
            } else if (this.chemical.dangercode.toUpperCase() === "YELLOW") {
                return urljoin(path, "warning.png");
            } else if (this.chemical.dangercode.toUpperCase() === "GREEN") {
                return urljoin(path, "success.png");
            }

            return urljoin(path, "unknown.png");
        },
        dangerColorStyle() {
            let color;

            if (this.chemical.dangercode.toUpperCase() === "RED") {
                color = "DarkSalmon";
            } else if (this.chemical.dangercode.toUpperCase() === "YELLOW") {
                color = "rgb(255,255,102)";
            } else if (this.chemical.dangercode.toUpperCase() === "GREEN") {
                color = "LimeGreen";
            } else {
                color = "DarkGray"
            }

            return {
                backgroundColor: color
            };
        }
    },
    asyncComputed: {
        isSafetyDatasheetMissing() {
            return axios.get(this.sdsUrl)
                .then(() => false)
                .catch(() => true);
        }
    },
    data() {
        return {
            id: this.$route.params.id,
            loading: true,
            error: null,
            chemical: null
        };
    },
    created() {
        const url = urljoin("/api/v1/chemicals", this.id);

        axios.get(url)
            .then(response => response.data, () => (this.error = true))
            .then(response => (this.chemical = response))
            .then(() => (this.loading = false));
    }
};
</script>

<style lang="scss" scoped>
@import '~bootstrap/scss/bootstrap';

.container { /* due to IE 11 */
    margin-left: 0;
    margin-right: 0;
    justify-content: center;
}

@media only print {
    #help-icon-container, #sds-link-button {
        display: none;
    }

    #dangercode-icon-container {
        display: inline-block !important;
    }

    #dangercode-container {
        display: inline-block;
    }
}
</style>

<i18n lang="yaml" locale="en">
showChemicalsTitle: "Chemicals Risk Assessment"
decommissioned: "DECOMMISSIONED"
moreInfo: "Click for more information"
dangerCode: "Danger Code: {color}"
red: "Red"
yellow: "Yellow"
green: "Green"
sdsRevisionDate: "SDS Revision Date"
malCode: "MAL Code"
ufi: "UFI"
pictograms: "Hazard Pictograms"
partnerReferences: "Partner references"
substances: "Substances"
carcenogenic: "Carcinogenic"
candidates: "Candidate Substances"
endocrineDisruptors: "Endocrine Disruptors"
occupationalHealthValue: "Organic solvents"
others: "Other Substances"
hAndEuhStatementSection: "Hazard Statements"
hstatements: "Hazard Statements"
euhstatements: "Additional Hazard Statements"
pstatements: "P-Statements"
skin: "Skin"
eyes: "Eyes"
inhalation: "Inhalation"
protection: "Protection"
chemicalResistantGloves: "Chemical Resistant Gloves"
goggles: "Goggles"
halfMaskRespirator: "Half mask with A filter - Organic solvents.<br> Half mask with B filter - Inorganic vapors.<br>Fresh air-supplied respirators when working in booths with spray equipment."
signalword: "Signal Word: {word}"
none: "None"
warning: "Warning"
danger: "Danger"
clickToFoldCollapse: "Click to fold/collapse"
sds: "Safety Datasheet"
goToSds: "Go to Safety DataSheet"
noSdsFound: "No SDS found"
</i18n>

<i18n lang="yaml" locale="dk">
showChemicalsTitle: "Kemisk risikovurdering"
decommissioned: "UDGÅET"
moreInfo: "Yderligere information"
dangerCode: "Farefarve: {color}"
red: "Rød"
yellow: "Gul"
green: "Grøn"
sdsRevisionDate: "SDS revisionsdato"
malCode: "MAL kode"
ufi: "UFI"
pictograms: "Farepiktogrammer"
partnerReferences: "Part nr."
substances: "Stoffer"
carcenogenic: "Kræftfremkaldende stoffer"
candidates: "Kandidatliste stoffer"
endocrineDisruptors: "Hormonforstyrrende stoffer"
occupationalHealthValue: "Organiske opløsningsmidler"
others: "Øvrige stoffer"
hAndEuhStatementSection: "Faresætninger"
hstatements: "Faresætninger"
euhstatements: "Supplerende faresætninger"
pstatements: "Sikkerhedssætninger"
skin: "Hud"
eyes: "Øjne"
inhalation: "Indånding"
protection: "Værnemidler"
chemicalResistantGloves: "Kemikalieresistente handsker."
goggles: "Briller (skærm) hvis relevant."
halfMaskRespirator: "Halvmaske med A filter - Organiske opløsningsmidler.<br>Halvmaske med B filter - Uorganiske dampe.<br>Friskluftsforsynet åndedrætsværn ved arbejde i kabiner med sprøjteudstyr."
signalword: "Signalord: {word}"
none: "Intet"
warning: "Advarsel"
danger: "Fare"
clickToFoldCollapse: "Ekspander/Kollaps"
sds: "Sikkerhedsdatablad"
goToSds: "Besøg"
noSdsFound: "Intet sikkerhedsdatablad fundet."
</i18n>
